/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';
import waitForGraphQL from 'js/lib/waitForGraphQL';
import { apolloFetchPolicy } from 'js/utils/apolloFetchPolicy';

import { breakpoints } from '@coursera/cds-core';

import DomainMetatags from 'bundles/browse-common/components/DomainMetatags';
import BrowseInternalLinking from 'bundles/browse/components/Domain/BrowseInternalLinking';
import DomainBanner from 'bundles/browse/components/Domain/DomainBanner';
import ProductOfferings from 'bundles/browse/components/Domain/ProductOfferings';
import SubdomainsBanner from 'bundles/browse/components/Domain/SubdomainsBanner';
import type { Domain } from 'bundles/browse/components/Domain/types/Domain';
import type { Subdomain } from 'bundles/browse/components/Domain/types/SubDomain';
import OfferExplanationTable from 'bundles/browse/components/OfferExplanationTable';
import { DomainSubdomainQuery } from 'bundles/browse/components/Queries';
import { withCourseraPlusOwnershipProvider } from 'bundles/browse/components/withBrowseCourseraPlusOwnerships';
import { BANNER_HEIGHT } from 'bundles/browse/constants';
import EnableNoStatePrefetch from 'bundles/enterprise-legacy-learner-home/components/performance/EnableNoStatePrefetch';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

const styles = {
  gridContainer: css`
    margin-right: auto;
    margin-left: auto;
    padding-left: 15px;
    padding-right: 15px;

    &::before,
    &::after {
      display: table;
      content: ' ';
    }

    &::after {
      clear: both;
    }

    @media (min-width: 768px) {
      width: 750px;
    }

    @media (min-width: 992px) {
      width: 970px;
    }

    @media (min-width: 1200px) {
      width: 1170px;
    }
  `,
  domainPage: () => css`
    position: relative;
    padding-bottom: 20px;
    min-height: 500px;
    display: block;
    width: 100%;

    .page-banner-wrapper {
      width: 100%;
      position: relative;

      ${breakpoints.up('sm')} {
        max-height: 480px;
      }
    }

    .product-offerings-wrapper {
      position: relative;
      min-height: 600px;

      .browse-faq-container {
        .rc-BrowseFAQs {
          margin-bottom: 0;
        }
      }
    }
  `,
};

type PropsFromRouter = {
  router: InjectedRouter;
};

type Props = PropsFromRouter & {
  domain?: Domain;
  subdomain?: Subdomain;
};

export const DomainPage = (props: Props) => {
  const { domain, subdomain, router } = props;
  const { subdomainSlug } = router?.params || {};

  if (!domain || (subdomainSlug && !subdomain)) {
    if (inServerContext) {
      throw new NotFoundError();
    } else {
      redirect.setLocation('/browse');
      return null;
    }
  }

  return (
    <section className="rc-DomainPage" data-testid="domain-page" css={styles.domainPage}>
      <div className="page-banner-wrapper" style={{ minHeight: BANNER_HEIGHT }}>
        {subdomain ? <SubdomainsBanner domain={domain} subdomain={subdomain} /> : <DomainBanner domain={domain} />}
      </div>
      <div className="product-offerings-wrapper">
        <ProductOfferings domain={domain} subdomain={subdomain} />
        <BrowseInternalLinking domain={domain} subdomain={subdomain} />
        {!inServerContext && (
          <div css={styles.gridContainer} className="body-container">
            <OfferExplanationTable />
          </div>
        )}
      </div>
      {/* For robots */}
      <DomainMetatags domain={domain} subdomain={subdomain} />
      <EnableNoStatePrefetch />
    </section>
  );
};

type graphqlProps = {
  data?: {
    SubdomainsV1Resource?: { slug?: { elements?: Array<Domain> } };
    DomainsV1Resource?: { slug?: { elements?: Array<Subdomain> } };
  };
};

export default compose<Props, {}>(
  connectToRouter((router) => ({ router })),
  withCourseraPlusOwnershipProvider,
  waitForGraphQL(DomainSubdomainQuery, {
    options: ({
      router: {
        params: { domainSlug, subdomainSlug },
      },
    }: PropsFromRouter) => ({
      ...apolloFetchPolicy({ cacheForLoggedOutOnly: false }),
      variables: {
        domainSlug: domainSlug || '',
        subdomainSlug: subdomainSlug || '',
        isDomainSlugDefined: typeof domainSlug === 'string',
        isSubdomainSlugDefined: typeof subdomainSlug === 'string',
      },
      errorPolicy: 'all',
    }),
    skip: ({ router: { params } }: PropsFromRouter) => !params.domainSlug && !params.subdomainSlug,
    props: ({ data }: graphqlProps) => ({
      subdomain: data?.SubdomainsV1Resource?.slug?.elements?.[0],
      domain: data?.DomainsV1Resource?.slug?.elements?.[0],
    }),
  })
)(DomainPage);
